// Month-grid math for the date-range picker. Pure and UTC by construction.
//
// Hand-rolled rather than a date-picker dependency: those work in `Date`
// instants in the BROWSER's zone, and this app is UTC end to end
// (`lib/time-range.ts`, the "(UTC)" range label). Each local conversion can
// shift the calendar day (see `lib/bucket-format.ts`'s header). A `CalDate` is
// three integers, never an instant, so it has no zone to get wrong.

/** A calendar day as plain integers. NOT an instant — there is deliberately
 *  no timezone, no clock reading, and no `Date` anywhere in this type. */
export interface CalDate {
  year: number;
  /** 1..12, not JS's 0-indexed month. */
  month: number;
  day: number;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/** The leading `YYYY-MM-DD` of a wire bound (a bare day OR a moment), or
 *  `null` for anything that isn't one. Reads the digits out of the string; a
 *  moment's time-of-day part is simply ignored, which is what a calendar
 *  wants. */
export function toCalDate(value: string | null | undefined): CalDate | null {
  if (!value) return null;
  const m = ISO_DAY.exec(value);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** `YYYY-MM-DD`, zero-padded — the bare-day wire shape. */
export function fromCalDate(d: CalDate): string {
  const mm = String(d.month).padStart(2, "0");
  const dd = String(d.day).padStart(2, "0");
  return `${d.year}-${mm}-${dd}`;
}

/** Negative / zero / positive, for sorting and range tests. Compares the
 *  fields, so it never builds a `Date` and never consults a zone. */
export function compareCalDates(a: CalDate, b: CalDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

export function isSameCalDate(a: CalDate | null, b: CalDate | null): boolean {
  return a !== null && b !== null && compareCalDates(a, b) === 0;
}

/** True iff `d` falls within `[a, b]` inclusive, in either given order — the
 *  caller may hand over a drag/hover pair that runs backwards. */
export function isWithinRange(d: CalDate, a: CalDate | null, b: CalDate | null): boolean {
  if (a === null || b === null) return false;
  const [lo, hi] = compareCalDates(a, b) <= 0 ? [a, b] : [b, a];
  return compareCalDates(d, lo) >= 0 && compareCalDates(d, hi) <= 0;
}

/** Today, as the UTC calendar sees it. `now` is injectable so callers and
 *  tests can pin it; it is the ONE place a real instant enters this module,
 *  and it leaves again immediately as three integers. */
export function todayUtc(now: Date = new Date()): CalDate {
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1, day: now.getUTCDate() };
}

/** `d` shifted by whole days, rolling across months and years correctly
 *  (including leap days). `Date.UTC` does the arithmetic — a fixed-offset
 *  calendar with no DST, so adding a day can never land on the same or a
 *  skipped date the way local-time arithmetic can. */
export function addDays(d: CalDate, delta: number): CalDate {
  const t = new Date(Date.UTC(d.year, d.month - 1, d.day + delta));
  return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
}

/** The (year, month) `delta` months away, clamping the day is NOT this
 *  function's job — it answers about the MONTH the grid should show. */
export function addMonths(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const t = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1 };
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Sunday-first, matching the `en-US` locale the rest of this app formats in
 *  (`Intl.DateTimeFormat("en-US", …)` throughout `lib/bucket-format.ts` and
 *  `lib/time-range.ts`) and the working week where this is read. */
export const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

/** Always SIX rows of seven, so the popover's height never changes as the
 *  reader pages through months (a grid that grows and shrinks moves the
 *  buttons under the cursor). Cells outside the month are `null` rather than
 *  the neighbouring month's days: a greyed-out "1" sitting under the August
 *  heading is a click a reader can make and then misread. */
export function monthMatrix(year: number, month: number): (CalDate | null)[][] {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const total = daysInMonth(year, month);
  const cells: (CalDate | null)[] = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= total; day += 1) cells.push({ year, month, day });
  while (cells.length < 42) cells.push(null);
  const weeks: (CalDate | null)[][] = [];
  for (let i = 0; i < 42; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const FULL_DAY = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** "August 2026" — the grid's own heading. */
export function monthLabel(year: number, month: number): string {
  return MONTH_YEAR.format(new Date(Date.UTC(year, month - 1, 1)));
}

/** "Sunday, August 9, 2026": a day cell's accessible name, since the
 *  visible label is just the number and "9" tells a screen reader nothing. */
export function calDateLabel(d: CalDate): string {
  return FULL_DAY.format(new Date(Date.UTC(d.year, d.month - 1, d.day)));
}

/** Replaces the `YYYY-MM-DD` portion of an in-progress `<input>` value while
 *  KEEPING any time-of-day the reader already typed. The calendar owns the
 *  day and the time inputs own the clock, so picking a new day must not
 *  silently reset a 14:30 the reader set a moment ago. An empty or
 *  unparseable value just becomes the bare day. */
export function withDatePart(inputValue: string, d: CalDate): string {
  const day = fromCalDate(d);
  const timePart = inputValue.length > 10 ? inputValue.slice(10) : "";
  return `${day}${timePart}`;
}
